import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button, Icon } from 'semantic-ui-react';
import { API_CONFIG, buildApiUrl } from '../utils/apiConfig';
import { normalizeWeatherbitForecast } from '../utils/forecast';
import { usePwaInstallPrompt } from '../hooks/usePwaInstallPrompt';

const SHOW_PWA_INSTALL_HINT = false;

const cardStyle = {
  border: '1px solid #d8e3f0',
  borderRadius: 16,
  background: '#ffffff',
  padding: 14,
  boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
};

const sectionTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  fontWeight: 900,
  color: '#1f2937',
  marginBottom: 8,
};

const outlineButtonStyle = {
  borderRadius: 8,
  border: '1px solid #0b6fcf',
  background: '#ffffff',
  color: '#0b6fcf',
  boxShadow: 'none',
  fontWeight: 800,
};

const getPortNames = (dayData) => (
  (dayData?.dayPocs || []).map((poc) => poc?.poc_info?.name).filter(Boolean)
);

const resolveMediaUrl = (value) => {
  if (!value) return null;
  const url = String(value).trim();
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  try {
    const apiOrigin = new URL(API_CONFIG.API_BASE_URL).origin;
    if (url.startsWith('/')) return `${apiOrigin}${url}`;
    if (url.startsWith('media/')) return `${apiOrigin}/${url}`;
    return new URL(url, API_CONFIG.API_BASE_URL).toString();
  } catch (_error) {
    return url;
  }
};

const getPortPhotoUrl = (portVisit) => resolveMediaUrl(
  portVisit?.poc_info?.photo_thumbnail_url ||
  portVisit?.poc_info?.photo_small_url ||
  portVisit?.poc_info?.thumbnail_url ||
  portVisit?.poc_info?.photo_url ||
  portVisit?.poc_info?.photo ||
  portVisit?.photo_thumbnail_url ||
  portVisit?.photo_small_url ||
  portVisit?.thumbnail_url ||
  portVisit?.photo_url ||
  portVisit?.photo
);

const getEventName = (event) => (
  event?.title ||
  event?.name ||
  event?.event_info?.title ||
  event?.event_info?.name ||
  event?.description ||
  'Scheduled event'
);

const getPortKey = (poc) => String(poc?.id || poc?.poc_id || poc?.poc_info?.id || poc?.poc_info?.name || '');

const getGeoJsonPointCoordinates = (point) => {
  const coords = point?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const lon = Number(coords[0]);
  const lat = Number(coords[1]);
  return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
};

const asNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const hasTimeComponent = (value) => /(?:T|\s)\d{1,2}:\d{2}/.test(String(value || ''));

const getWeatherbitWindow = (poc, dayData) => {
  const start = poc?.day_arrival || dayData?.dayStart || dayData?.date || dayData?.dayKey;
  const end = poc?.day_departure || (hasTimeComponent(start) ? start : dayData?.dayEnd || start);
  const startDate = start ? new Date(start) : null;
  const endDate = end ? new Date(end) : null;
  if (!startDate || Number.isNaN(startDate.getTime())) return null;
  if (!hasTimeComponent(start)) startDate.setUTCHours(0, 0, 0, 0);
  if (!endDate || Number.isNaN(endDate.getTime())) return { start: startDate, end: startDate };
  if (!hasTimeComponent(end)) endDate.setUTCHours(23, 59, 59, 999);
  return { start: startDate, end: endDate < startDate ? startDate : endDate };
};

const getWeatherbitTimestamp = (item) => {
  const raw = item?.timestamp_utc || item?.timestamp_local || item?.datetime;
  if (!raw) return null;
  const normalized = String(raw).includes('T') ? raw : String(raw).replace(' ', 'T');
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  const date = new Date(item?.timestamp_utc && !hasTimezone ? `${normalized}Z` : normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const summarizeWeatherbitForecast = (snapshot, poc, dayData) => {
  const data = snapshot?.forecast?.data;
  if (!Array.isArray(data) || !data.length) return null;

  const windowInfo = getWeatherbitWindow(poc, dayData);
  const points = windowInfo
    ? data.filter((item) => {
        const timestamp = getWeatherbitTimestamp(item);
        return timestamp && timestamp >= windowInfo.start && timestamp <= windowInfo.end;
      })
    : data;
  const rows = points.length ? points : data.slice(0, 24);
  if (!rows.length) return null;

  const temps = rows.map((item) => asNumber(item.temp)).filter((value) => value !== null);
  const precipitation = rows.map((item) => asNumber(item.precip)).filter((value) => value !== null);
  const popValues = rows.map((item) => asNumber(item.pop)).filter((value) => value !== null);
  const windValues = rows.map((item) => asNumber(item.wind_spd)).filter((value) => value !== null);
  const description = rows.find((item) => item?.weather?.description)?.weather?.description;
  const parts = [];

  if (temps.length) {
    const min = Math.min(...temps);
    const max = Math.max(...temps);
    parts.push(Math.round(min) === Math.round(max) ? `${Math.round(max)}°C` : `${Math.round(min)}-${Math.round(max)}°C`);
  }
  if (description) parts.push(description);

  const totalPrecip = precipitation.reduce((sum, value) => sum + value, 0);
  if (totalPrecip > 0.1) {
    parts.push(`${Math.round(totalPrecip)}mm rain`);
  } else if (popValues.length) {
    const maxPop = Math.max(...popValues);
    if (maxPop >= 20) parts.push(`${Math.round(maxPop)}% rain`);
  }

  if (windValues.length) {
    parts.push(`${Math.round(Math.max(...windValues))} m/s wind`);
  }

  return parts.join(' · ') || null;
};

export default function MobileTourUserView({
  activeTourToShow,
  mobileTab,
  setMobileTab,
  selectedDayData,
  currentDayData,
  yesterdayDayData,
  tomorrowDayData,
  currentTourPosition,
  currentShipActivity,
  formatDayTitle,
  formatDistanceForSegment,
  getDayEvents,
  getDayWeatherSummary,
  renderDayResourceList,
  renderWeatherDetails,
  openWeatherDetails,
  renderUpcomingPanel,
  renderMapScreen,
  renderFilesScreen,
  selectGuestDay,
  focusTrackingLocation,
  focusTourEventOnMap,
  getApiHeaders,
  isFullTourPending = false,
}) {
  const {
    canInstall,
    dismissPrompt,
    isIos,
    isStandalone,
    promptInstall,
    shouldShowPrompt,
  } = usePwaInstallPrompt();
  const dayData = selectedDayData || currentDayData;
  const dayEvents = useMemo(() => getDayEvents?.(dayData) || [], [dayData, getDayEvents]);
  const routePorts = getPortNames(dayData);
  const activeSegments = (dayData?.segmentEntries || []).filter(Boolean);
  const firstSegment = activeSegments[0] || null;
  const totalSegmentDistance = activeSegments.length
    ? formatDistanceForSegment?.({
        ...activeSegments[0],
        segment: {
          ...(activeSegments[0]?.segment || {}),
          distance_km: activeSegments.reduce((total, entry) => {
            const value = Number(entry?.segment?.distance_km);
            return Number.isFinite(value) ? total + value : total;
          }, 0),
        },
      })
    : null;
  const dayPorts = (dayData?.dayPocs || []).filter((poc) => poc?.poc_info?.name);
  const weatherbitRequestCacheRef = useRef(new Map());
  const weatherbitRequestInFlightRef = useRef(new Set());
  const linkedEventPorts = activeTourToShow?.mobile_initial?.linked_event_ports || {};
  const linkedEventDestinationById = useMemo(() => {
    const map = new Map();
    (activeTourToShow?.ports_of_call || []).forEach((poc) => {
      const templateId = poc?.poc_info?.id;
      if (templateId != null) map.set(String(templateId), poc);
    });
    Object.entries(linkedEventPorts || {}).forEach(([id, port]) => {
      if (!map.has(String(id))) {
        map.set(String(id), {
          id: `event-destination-${id}`,
          visit_order: null,
          day_arrival: dayData?.date || dayData?.dayKey || null,
          day_departure: dayData?.date || dayData?.dayKey || null,
          effective_location: port?.location || null,
          poc_info: port,
          events: [],
          is_event_destination: true,
        });
      }
    });
    return map;
  }, [activeTourToShow?.ports_of_call, dayData?.date, dayData?.dayKey, linkedEventPorts]);
  const getLinkedEventDestinationPort = (event, anchorPoc = null) => {
    const anchorTemplateId = anchorPoc?.poc_info?.id != null ? String(anchorPoc.poc_info.id) : '';
    const destinationId = (event?.event_info?.port_of_call_ids || [])
      .map((id) => String(id))
      .find((id) => id && id !== anchorTemplateId && linkedEventDestinationById.has(id));
    return destinationId ? linkedEventDestinationById.get(destinationId) : null;
  };
  const getEventDestinationPort = (event, anchorPoc = null) => {
    if (!event?.display_as_port_card && !event?.event_info?.display_as_port_card) return null;
    return getLinkedEventDestinationPort(event, anchorPoc);
  };
  const destinationEventItems = useMemo(() => (
    dayEvents
      .map((item) => ({ ...item, destinationPoc: getEventDestinationPort(item.event, item.poc) }))
      .filter((item) => item.destinationPoc)
  ), [dayEvents, linkedEventDestinationById]);
  const destinationEventIds = useMemo(
    () => new Set(destinationEventItems.map((item) => String(item.event?.id || ''))),
    [destinationEventItems]
  );
  const inlineDayEvents = useMemo(
    () => dayEvents.filter((item) => !destinationEventIds.has(String(item.event?.id || ''))),
    [dayEvents, destinationEventIds]
  );
  const destinationWeatherPorts = useMemo(() => {
    const byKey = new Map();
    dayEvents.forEach(({ event, poc }) => {
      const destinationPoc = getLinkedEventDestinationPort(event, poc);
      const key = getPortKey(destinationPoc);
      if (key && !byKey.has(key)) byKey.set(key, destinationPoc);
    });
    return [...byKey.values()];
  }, [dayEvents, linkedEventDestinationById]);
  const destinationWeatherKey = useMemo(() => (
    destinationWeatherPorts.map((poc) => {
      const coords = getGeoJsonPointCoordinates(poc?.effective_location || poc?.poc_info?.location);
      return `${getPortKey(poc)}:${coords?.lat ?? ''},${coords?.lon ?? ''}`;
    }).join('|')
  ), [destinationWeatherPorts]);
  const [weatherbitByPortId, setWeatherbitByPortId] = useState({});
  const [weatherbitLoadingByPortId, setWeatherbitLoadingByPortId] = useState({});
  const weatherbitForecastByPortId = useMemo(() => {
    const next = {};
    Object.entries(weatherbitByPortId || {}).forEach(([key, snapshot]) => {
      const normalized = normalizeWeatherbitForecast(snapshot);
      if (normalized?.hourly) next[String(key)] = normalized;
    });
    return next;
  }, [weatherbitByPortId]);
  const weatherDayData = useMemo(() => {
    if (!dayData) return dayData;
    let changed = false;
    const hydratePoc = (poc) => {
      if (!poc) return poc;
      const key = String(poc?.id || '');
      const forecast = weatherbitForecastByPortId[key];
      if (!forecast?.hourly || poc?.weather?.hourly) return poc;
      changed = true;
      return { ...poc, weather: forecast };
    };
    const hydratedPocs = (dayData.dayPocs || []).map(hydratePoc);
    const hydratedSegments = (dayData.segmentEntries || []).map((entry) => {
      const fromPoc = hydratePoc(entry?.fromPoc);
      const toPoc = hydratePoc(entry?.toPoc);
      if (fromPoc === entry?.fromPoc && toPoc === entry?.toPoc) return entry;
      changed = true;
      return { ...entry, fromPoc, toPoc };
    });
    return changed
      ? { ...dayData, dayPocs: hydratedPocs, segmentEntries: hydratedSegments }
      : dayData;
  }, [dayData, weatherbitForecastByPortId]);
  const weatherSummary = getDayWeatherSummary?.(weatherDayData);
  const weatherbitPortIdsKey = (dayData?.dayPocs || []).map((poc) => poc?.id).filter(Boolean).join(',');
  const isWeatherbitLoading = dayPorts.some((poc) => weatherbitLoadingByPortId[String(poc?.id || '')]);
  const hasWeatherForecast = (weatherDayData?.dayPocs || []).some((poc) => poc?.weather?.hourly);

  useEffect(() => {
    if ((!weatherbitPortIdsKey && !destinationWeatherPorts.length) || !getApiHeaders) {
      setWeatherbitByPortId({});
      setWeatherbitLoadingByPortId({});
      return undefined;
    }

    let cancelled = false;
    const requestKey = `${weatherbitPortIdsKey}|${destinationWeatherKey}`;
    const portIds = weatherbitPortIdsKey.split(',').filter(Boolean);
    const destinationPorts = destinationWeatherPorts
      .map((poc) => ({
        poc,
        key: getPortKey(poc),
        coords: getGeoJsonPointCoordinates(poc?.effective_location || poc?.poc_info?.location),
      }))
      .filter((item) => item.key && item.coords);
    const targetKeys = [...portIds, ...destinationPorts.map((item) => item.key)];
    const cachedResult = weatherbitRequestCacheRef.current.get(requestKey);
    if (cachedResult) {
      setWeatherbitByPortId((prev) => ({ ...prev, ...cachedResult }));
      setWeatherbitLoadingByPortId((prev) => ({
        ...prev,
        ...Object.fromEntries(targetKeys.map((id) => [id, false])),
      }));
      return undefined;
    }
    if (weatherbitRequestInFlightRef.current.has(requestKey)) return undefined;
    weatherbitRequestInFlightRef.current.add(requestKey);

    setWeatherbitLoadingByPortId((prev) => ({
      ...prev,
      ...Object.fromEntries(targetKeys.map((id) => [id, true])),
    }));

    const loadWeatherbit = async () => {
      try {
        const headers = await getApiHeaders();
        const response = weatherbitPortIdsKey
          ? await axios({
              url: buildApiUrl(`weatherbit-forecast/?port_ids=${encodeURIComponent(weatherbitPortIdsKey)}`),
              method: 'get',
              headers,
            })
          : null;
        if (cancelled) return;

        const nextByPortId = {};
        (response?.data?.results || []).forEach((item) => {
          if (item?.port_id && item?.forecast) {
            nextByPortId[String(item.port_id)] = item;
          }
        });
        const destinationResults = await Promise.all(destinationPorts.map(async ({ key, coords }) => {
          try {
            const destinationResponse = await axios({
              url: buildApiUrl(`weatherbit-forecast/?lat=${encodeURIComponent(coords.lat)}&lon=${encodeURIComponent(coords.lon)}`),
              method: 'get',
              headers,
            });
            return { key, item: destinationResponse.data };
          } catch {
            return { key, item: null };
          }
        }));
        if (cancelled) return;
        destinationResults.forEach(({ key, item }) => {
          if (item?.forecast) nextByPortId[key] = item;
        });
        weatherbitRequestCacheRef.current.set(requestKey, nextByPortId);
        setWeatherbitByPortId((prev) => ({ ...prev, ...nextByPortId }));
      } catch {
        if (!cancelled) {
          setWeatherbitByPortId((prev) => ({ ...prev }));
        }
      } finally {
        weatherbitRequestInFlightRef.current.delete(requestKey);
        if (!cancelled) {
          setWeatherbitLoadingByPortId((prev) => ({
            ...prev,
            ...Object.fromEntries(targetKeys.map((id) => [id, false])),
          }));
        }
      }
    };

    loadWeatherbit();
    return () => {
      cancelled = true;
    };
  }, [destinationWeatherKey, weatherbitPortIdsKey, getApiHeaders]);

  const showDayOnMap = (targetDay = dayData, { focusPort = null } = {}) => {
    if (!targetDay) return;
    setMobileTab('map');
    const focus = () => {
      selectGuestDay?.(targetDay, focusPort ? { focusPort, focusZoom: 14.2 } : undefined);
    };
    window.setTimeout(focus, focusPort ? 80 : 0);
    if (focusPort) window.setTimeout(focus, 260);
  };

  const findSegmentBetweenPorts = (fromPoc, toPoc) => {
    const fromKey = getPortKey(fromPoc);
    const toKey = getPortKey(toPoc);
    return activeSegments.find((entry) => (
      getPortKey(entry?.fromPoc) === fromKey &&
      getPortKey(entry?.toPoc) === toKey
    ));
  };

  const showShipOnMap = () => {
    setMobileTab('map');
    focusTrackingLocation?.();
  };

  const openWeatherSheet = () => {
    openWeatherDetails?.(weatherDayData);
  };

  const renderTodaySummary = () => (
    <div style={{ ...cardStyle, background: '#ffffff' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 900, color: '#111827', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {activeSegments.length ? 'Cruising day' : 'Port day'}
            {totalSegmentDistance ? ` - ${totalSegmentDistance}` : ''}
          </div>
          <div style={{ marginTop: 5, color: '#64748b', fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {routePorts.length ? routePorts.join(' -> ') : 'Day at sea'}
          </div>
        </div>
        <Button
          size='small'
          icon='map marker alternate'
          content='Show on map'
          onClick={() => showDayOnMap(dayData)}
          style={{ ...outlineButtonStyle, margin: 0 }}
          disabled={!dayData}
        />
      </div>
    </div>
  );

  const renderPwaInstallCard = () => {
    if (!SHOW_PWA_INSTALL_HINT || !shouldShowPrompt) return null;

    const copy = canInstall
      ? {
          title: 'Install QR Maps',
          body: 'Add this tour view to the home screen for faster launch, full-screen navigation, and better offline access on Android.',
          action: 'Install app',
        }
      : {
          title: 'Add to Home Screen',
          body: 'On iPhone or iPad, open Share and choose Add to Home Screen to launch QR Maps like an app and keep the cruise view easier to reopen.',
          action: null,
        };

    return (
      <div
        style={{
          ...cardStyle,
          borderColor: '#bfdbfe',
          background: 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)',
          display: 'grid',
          gap: 12,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr) auto', gap: 10, alignItems: 'start' }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: '#0b6fcf',
              color: '#ffffff',
              display: 'grid',
              placeItems: 'center',
              boxShadow: '0 10px 20px rgba(11, 111, 207, 0.18)',
            }}
          >
            <Icon name={isIos ? 'share square' : 'mobile alternate'} style={{ margin: 0, fontSize: 16 }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>{copy.title}</div>
            <div style={{ marginTop: 5, fontSize: 13, lineHeight: 1.5, color: '#334155' }}>{copy.body}</div>
          </div>
          <button
            type='button'
            onClick={dismissPrompt}
            aria-label='Dismiss install prompt'
            style={{
              border: 0,
              background: 'transparent',
              color: '#64748b',
              cursor: 'pointer',
              padding: 2,
            }}
          >
            <Icon name='close' style={{ margin: 0 }} />
          </button>
        </div>

        {canInstall ? (
          <Button
            fluid
            icon='download'
            content={copy.action}
            onClick={() => { void promptInstall(); }}
            style={{
              borderRadius: 10,
              background: '#0b6fcf',
              color: '#ffffff',
              fontWeight: 900,
              boxShadow: '0 12px 24px rgba(11, 111, 207, 0.22)',
            }}
          />
        ) : isIos ? (
          <div
            style={{
              display: 'grid',
              gap: 6,
              fontSize: 12,
              color: '#475569',
              background: 'rgba(255,255,255,0.85)',
              border: '1px solid #dbeafe',
              borderRadius: 12,
              padding: '10px 12px',
            }}
          >
            <div><b>1.</b> Tap <Icon name='share square' style={{ margin: '0 2px' }} /> Share in Safari.</div>
            <div><b>2.</b> Choose <b>Add to Home Screen</b>.</div>
            <div><b>3.</b> Open QR Maps from the new icon for a full-screen app view.</div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderSailingStretchCard = (entry, index, fallbackFromPoc = null, fallbackToPoc = null) => {
    const fromPoc = entry?.fromPoc || fallbackFromPoc;
    const toPoc = entry?.toPoc || fallbackToPoc;
    const fromName = fromPoc?.poc_info?.name || 'Port';
    const toName = toPoc?.poc_info?.name || 'Port';
    const distance = entry ? formatDistanceForSegment?.(entry) : null;
    return (
      <div
        key={entry?.key || `sailing-${getPortKey(fromPoc)}-${getPortKey(toPoc)}-${index}`}
        style={{
          border: '1px solid #dbeafe',
          borderRadius: 14,
          background: '#f8fbff',
          padding: '10px 12px',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr) auto', gap: 8, alignItems: 'center' }}>
          <Icon name='ship' style={{ color: '#0284c7', fontSize: 16, margin: 0 }} />
          <div style={{ minWidth: 0, color: '#111827', fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fromName} {'->'} {toName}
          </div>
          {distance ? (
            <div style={{ color: '#334155', fontWeight: 900, whiteSpace: 'nowrap' }}>
              {distance}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderExcursionStretchCard = (event, fromPoc, toPoc, index) => {
    const fromName = fromPoc?.poc_info?.name || 'Port';
    const toName = toPoc?.poc_info?.name || getEventName(event);
    return (
      <div
        key={`event-stretch-${event?.id || index}-${getPortKey(toPoc)}`}
        style={{
          border: '1px solid #ffedd5',
          borderRadius: 14,
          background: '#fff7ed',
          padding: '10px 12px',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', gap: 8, alignItems: 'center' }}>
          <Icon name='road' style={{ color: '#ea580c', fontSize: 16, margin: 0 }} />
          <div style={{ minWidth: 0, color: '#111827', fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fromName} {'->'} {toName}
          </div>
        </div>
      </div>
    );
  };

  const renderPortItineraryCard = (poc, index) => {
    const photoUrl = getPortPhotoUrl(poc);
    const weatherbitSnapshot = weatherbitByPortId[String(poc?.id || '')];
    const weatherbitSummary = summarizeWeatherbitForecast(weatherbitSnapshot, poc, dayData);
    const portWeather = weatherbitSummary || (
      weatherbitLoadingByPortId[String(poc?.id || '')] ? 'Loading Weatherbit forecast...' : null
    );
    const portName = poc?.poc_info?.name || 'Port';
    return (
      <div
        key={`port-${getPortKey(poc)}-${index}`}
        style={{
          ...cardStyle,
          position: 'relative',
          padding: 0,
          overflow: 'hidden',
          borderColor: '#064e8a',
        }}
      >
        <div
          style={{
            position: 'relative',
            minHeight: 142,
            background: photoUrl
              ? '#0f172a'
              : 'linear-gradient(135deg, rgba(2,34,75,0.98) 0%, rgba(5,91,151,0.92) 62%, rgba(14,116,144,0.76) 100%)',
          }}
        >
          {photoUrl ? (
            <img
              src={photoUrl}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : null}
          {!photoUrl ? (
            <div
              aria-hidden='true'
              style={{
                position: 'absolute',
                right: -24,
                bottom: -28,
                width: 180,
                height: 88,
                borderRadius: '55% 45% 0 0',
                background: 'linear-gradient(135deg, rgba(251,146,60,0.55), rgba(14,165,233,0.15))',
                opacity: 0.75,
              }}
            />
          ) : null}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              background: photoUrl
                ? 'linear-gradient(90deg, rgba(2, 6, 23, 0.82) 0%, rgba(7, 89, 133, 0.64) 52%, rgba(2, 6, 23, 0.28) 100%)'
                : 'transparent',
            }}
          />
          <Icon name='sun outline' style={{ position: 'absolute', right: 18, top: 20, color: '#fbbf24', fontSize: 34, margin: 0 }} />
          <div style={{ position: 'relative', minHeight: 142, padding: '16px 16px 14px', color: '#ffffff', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
            <div style={{ fontSize: 24, lineHeight: 1.08, fontWeight: 900, textShadow: '0 1px 2px rgba(2, 6, 23, 0.32)' }}>
              {portName}
            </div>
            <div style={{ marginTop: 8, color: '#dbeafe', fontWeight: 800 }}>
              {portWeather || 'Weather will appear when available'}
            </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <Button
                size='small'
                icon='map outline'
                content='Open map'
                onClick={() => showDayOnMap(dayData, { focusPort: poc })}
                style={{ borderRadius: 8, fontWeight: 900 }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderItineraryCards = () => {
    if (!dayPorts.length && !activeSegments.length) return null;
    if (!dayPorts.length) {
      return (
        <div style={{ display: 'grid', gap: 10 }}>
          {activeSegments.map((entry, index) => renderSailingStretchCard(entry, index))}
        </div>
      );
    }

    const cards = [];
    dayPorts.forEach((poc, index) => {
      cards.push(renderPortItineraryCard(poc, index));
      destinationEventItems
        .filter((item) => String(item.poc?.id || '') === String(poc?.id || ''))
        .forEach((item, eventIndex) => {
          cards.push(renderExcursionStretchCard(item.event, poc, item.destinationPoc, `${index}-${eventIndex}`));
          cards.push(renderPortItineraryCard(item.destinationPoc, `event-${item.event?.id || eventIndex}`));
        });
      const nextPoc = dayPorts[index + 1];
      if (nextPoc) {
        cards.push(renderSailingStretchCard(findSegmentBetweenPorts(poc, nextPoc), index, poc, nextPoc));
      }
    });

    return (
      <div style={{ display: 'grid', gap: 10 }}>
        {cards}
      </div>
    );
  };

  const renderShipOrSegment = () => {
    if (!currentTourPosition) return null;
    const updatedAt = currentTourPosition.recorded_at || currentTourPosition.received_at;
    return (
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>
          <Icon name={currentTourPosition.source === 'manual' ? 'map marker alternate' : 'ship'} style={{ color: '#075985' }} />
          Ship position
        </div>
        {updatedAt ? <div style={{ color: '#64748b', fontSize: 12 }}>Updated {new Date(updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div> : null}
        {currentShipActivity?.detail ? (
          <div style={{ marginTop: 4, color: '#334155', fontWeight: 700 }}>{currentShipActivity.detail}</div>
        ) : firstSegment?.toPoc?.poc_info?.name ? (
          <div style={{ marginTop: 4, color: '#334155', fontWeight: 700 }}>Sailing toward {firstSegment.toPoc.poc_info.name}</div>
        ) : (
          <div style={{ marginTop: 4, color: '#334155' }}>Latest position available</div>
        )}
        <Button size='small' icon='ship' content='Show ship on map' onClick={showShipOnMap} style={{ ...outlineButtonStyle, marginTop: 10 }} />
      </div>
    );
  };

  const renderSchedule = () => (
    <div style={cardStyle}>
      <div style={sectionTitleStyle}>
        <Icon name='clock outline' style={{ color: '#0b6fcf' }} />
        Day's schedule
      </div>
      {inlineDayEvents.length ? (
        <div style={{ display: 'grid', gap: 9 }}>
          {inlineDayEvents.slice(0, 5).map(({ event, poc }, index) => {
            const destinationPoc = getLinkedEventDestinationPort(event, poc);
            const destinationKey = getPortKey(destinationPoc);
            const destinationWeather = destinationKey ? weatherbitByPortId[destinationKey] : null;
            const destinationWeatherSummary = destinationPoc
              ? summarizeWeatherbitForecast(destinationWeather, destinationPoc, dayData)
              : null;
            const destinationWeatherLoading = destinationKey && weatherbitLoadingByPortId[destinationKey];

            return (
              <div key={`${event?.id || index}-${poc?.id || 'poc'}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'center' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, color: '#111827' }}>{getEventName(event)}</div>
                  <div style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>{destinationPoc?.poc_info?.name || poc?.poc_info?.name}</span>
                    {destinationWeatherSummary ? <span><Icon name='cloud' style={{ marginRight: 3 }} />{destinationWeatherSummary}</span> : null}
                    {!destinationWeatherSummary && destinationWeatherLoading ? <span>Loading forecast…</span> : null}
                  </div>
                </div>
                <Button
                  size='mini'
                  icon='map marker alternate'
                  content='Map'
                  onClick={() => focusTourEventOnMap?.(event, poc)}
                  disabled={!event?.id}
                  style={{ ...outlineButtonStyle, margin: 0, padding: '7px 9px' }}
                />
              </div>
            );
          })}
        </div>
      ) : isFullTourPending ? (
        <div style={{ minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#64748b', fontWeight: 700 }}>
          <Icon loading name='spinner' style={{ margin: 0 }} />
          <span>Loading schedule…</span>
        </div>
      ) : (
        <div style={{ color: '#64748b' }}>No scheduled tours yet.</div>
      )}
    </div>
  );

  const renderWeather = () => {
    if (!hasWeatherForecast && !isWeatherbitLoading) {
      return (
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name='cloud' style={{ color: '#64748b', fontSize: 22, margin: 0 }} />
          <div style={{ color: '#64748b', fontWeight: 800 }}>Weather forecast not available</div>
        </div>
      );
    }

    return (
      <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
          <Icon name='sun outline' style={{ color: '#f59e0b', fontSize: 22, margin: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 900, color: '#1f2937' }}>Weather</div>
            <div style={{ color: weatherSummary ? '#334155' : '#64748b', fontWeight: weatherSummary ? 700 : 500, fontSize: 13 }}>
              {weatherSummary || (isWeatherbitLoading ? 'Loading forecast...' : 'Forecast appears closer to the visit date.')}
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          marginTop: 12,
          border: '1px solid #dbeafe',
          borderRadius: 16,
          padding: '10px 7px',
          background: '#ffffff',
          overflow: 'hidden',
        }}
      >
        {renderWeatherDetails?.(weatherDayData, { mobile: true })}
      </div>
      <button
        type='button'
        onClick={openWeatherSheet}
        disabled={!hasWeatherForecast}
        style={{
          marginTop: 10,
          width: '100%',
          border: '1px solid #bfdbfe',
          borderRadius: 10,
          background: '#eff6ff',
          color: '#0b6fcf',
          fontWeight: 900,
          padding: '10px 12px',
          cursor: hasWeatherForecast ? 'pointer' : 'not-allowed',
          opacity: hasWeatherForecast ? 1 : 0.55,
        }}
      >
        Details
      </button>
    </div>
    );
  };

  const renderTodayFiles = () => (
    <div style={cardStyle}>
      <div style={sectionTitleStyle}>
        <Icon name='map outline' style={{ color: '#0b6fcf' }} />
        Day's maps & files
      </div>
      {renderDayResourceList?.(dayData, { mobile: true })}
      <Button fluid icon='folder open outline' content='Open all files' onClick={() => setMobileTab('files')} style={{ ...outlineButtonStyle, marginTop: 10 }} />
    </div>
  );

  const renderAdjacentDayButton = (targetDayData, label, direction) => {
    if (!targetDayData) return null;
    const names = getPortNames(targetDayData);
    const isPrevious = direction === 'previous';
    const content = (
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', color: '#0b6fcf', fontWeight: 900 }}>{label}</span>
        <span style={{ display: 'block', fontWeight: 900, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {names.length ? names.join(' -> ') : 'Day at sea'}
        </span>
        <span style={{ display: 'block', marginTop: 4, color: '#64748b', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {getDayWeatherSummary?.(targetDayData) || formatDayTitle?.(targetDayData)}
        </span>
      </span>
    );
    return (
      <div style={cardStyle}>
        <button type='button' onClick={() => {
          setMobileTab('today');
          selectGuestDay?.(targetDayData, { focusMap: false });
        }} style={{ border: 0, background: 'transparent', padding: 0, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isPrevious ? '14px minmax(0, 1fr)' : 'minmax(0, 1fr) 14px',
              gap: 8,
              alignItems: 'center',
            }}
          >
            {isPrevious ? <Icon name='chevron left' style={{ color: '#94a3b8', margin: 0 }} /> : null}
            {content}
            {!isPrevious ? <Icon name='chevron right' style={{ color: '#94a3b8', margin: 0 }} /> : null}
          </div>
        </button>
      </div>
    );
  };

  const renderAdjacentDays = () => {
    if (!yesterdayDayData && !tomorrowDayData) return null;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: yesterdayDayData && tomorrowDayData ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 10 }}>
        {renderAdjacentDayButton(yesterdayDayData, 'Previous day', 'previous')}
        {renderAdjacentDayButton(tomorrowDayData, 'Next day', 'next')}
      </div>
    );
  };

  const renderTodayScreen = () => (
    <div className='TourUser-mobile-today' style={{ display: 'grid', gap: 10 }}>
      <div style={{ padding: '4px 4px 2px', display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr)', gap: 8, alignItems: 'center' }}>
        <Icon name='ship' style={{ color: '#0b3b75', fontSize: 24, margin: 0 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: '#111827', lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeTourToShow?.name}</div>
          <div style={{ marginTop: 3, color: '#64748b', fontWeight: 700, fontSize: 12 }}>{formatDayTitle?.(dayData)}</div>
        </div>
      </div>
      {renderPwaInstallCard()}
      {renderTodaySummary()}
      {renderItineraryCards()}
      {renderShipOrSegment()}
      {renderSchedule()}
      {renderWeather()}
      {renderTodayFiles()}
      {renderAdjacentDays()}
    </div>
  );

  const tabs = [
    ['today', 'Today', 'home'],
    ['upcoming', 'Upcoming', 'calendar alternate outline'],
    ['map', 'Map', 'map outline'],
    ['files', 'Files', 'folder open'],
  ];

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: isStandalone ? '#edf4fb' : '#f4f7fb',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: '14px 10px 14px' }}>
        {mobileTab === 'today' ? renderTodayScreen() : null}
        {mobileTab === 'upcoming' ? renderUpcomingPanel({ mobile: true }) : null}
        {mobileTab === 'map' ? renderMapScreen() : null}
        {mobileTab === 'files' ? renderFilesScreen({ mobile: true }) : null}
      </div>

      <div
        className='TourUser-mobile-nav'
        style={{
          flex: '0 0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 4,
          padding: '6px 6px calc(6px + env(safe-area-inset-bottom, 0px))',
          borderTop: '1px solid #e5e7eb',
          background: '#ffffff',
          boxShadow: '0 -8px 22px rgba(15, 23, 42, 0.08)',
        }}
      >
        {tabs.map(([key, label, icon]) => (
          <button
            type='button'
            key={key}
            onClick={() => setMobileTab(key)}
            style={{
              border: 0,
              borderRadius: 10,
              background: 'transparent',
              color: mobileTab === key ? '#0b6fcf' : '#475569',
              padding: '7px 2px',
              display: 'grid',
              justifyItems: 'center',
              gap: 2,
              fontSize: 11,
              fontWeight: mobileTab === key ? 900 : 700,
              minWidth: 0,
            }}
          >
            <Icon name={icon} style={{ margin: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
              {label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
